"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { StringField } from "@/components/shared/FormFields";
import SpeakerButton from "@/components/ui/SpeakerButton";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import useSpeechRecognition from "@/hooks/useSpeechRecognition";
import { cn } from "@/utils/utils";

/* =====================================================
   PRONUNCIATION EXERCISE – FINAL STABLE VERSION
===================================================== */

export default function PronunciationExercise({
  exercise = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResult = false,
  onComplete = () => {},
  setAttempt = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= QUESTIONS ================= */
  const questions = Array.isArray(exercise.pronunciationQuestions)
    ? exercise.pronunciationQuestions.map((q) => ({
        textToPronounce: q.textToPronounce?.trim() || "",
        ipaSound: q.ipaSound?.trim() || "",
        requireCapitalization: !!q.requireCapitalization,
      }))
    : [];

  /* ================= SPEECH ================= */
  const recognition = useSpeechRecognition() || {};
const [liveTranscript, setLiveTranscript] = useState("");

  const {
    transcript = "",
    listening = false,
    startListening = () => {},
    stopListening = () => {},
    resetTranscript = () => {},
    browserSupportsSpeechRecognition = false,
  } = recognition;

  const speechSupported =
    typeof window !== "undefined" &&
    (browserSupportsSpeechRecognition ||
      "SpeechRecognition" in window ||
      "webkitSpeechRecognition" in window);

  /* ================= STATE ================= */
  const [answers, setAnswers] = useState({});
  const [recordingIndex, setRecordingIndex] = useState(null);
  const [dots, setDots] = useState("");
  const activeIndexRef = useRef(null);
  const completedRef = useRef(false);


  const compareWords = (spoken = "", expected = "", requireCap = false) => {
  const clean = (t) => {
    let s = t.trim();
    if (!requireCap) s = s.toLowerCase();
    return s.replace(/[^\w\s]/g, "");
  };

  const spokenWords = clean(spoken).split(/\s+/);
  const expectedWords = clean(expected).split(/\s+/);

  return expectedWords.map((word, i) => {
    if (spokenWords[i] === word) {
      return { word, status: "correct" };
    }
    if (!spokenWords[i]) {
      return { word, status: "missing" };
    }
    return { word, status: "wrong" };
  });
};


  /* ================= NORMALIZE ================= */
  const normalize = (text = "", requireCap = false) => {
    let t = String(text || "").trim();
    if (!requireCap) t = t.toLowerCase();
    return t.replace(/[^\w\s]/g, "").replace(/\s+/g, " ");
  };

  /* ================= TEXT TO SPEECH ================= */
  const speakText = (text, lang = "en-GB") => {
    if (typeof window === "undefined") return;
    const synth = window.speechSynthesis;
    if (!synth) return;

    synth.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = lang;
    utterance.rate = 0.9;

    const voices = synth.getVoices();
    const voice =
      voices.find((v) => v.lang === lang) ||
      voices.find((v) => v.lang.startsWith(lang.split("-")[0]));
    if (voice) utterance.voice = voice;

    if (voices.length === 0) {
      synth.onvoiceschanged = () => synth.speak(utterance);
    } else {
      synth.speak(utterance);
    }
  };

  /* ================= MIC PERMISSION ================= */
  useEffect(() => {
    if (typeof window === "undefined") return;
    navigator.mediaDevices?.getUserMedia({ audio: true }).catch(() => {});
  }, []);

  /* ================= RECORDING DOTS ================= */
  useEffect(() => {
    if (recordingIndex === null) {
      setDots("");
      return;
    }
    const interval = setInterval(() => {
      setDots((d) => (d.length >= 3 ? "" : d + "."));
    }, 500);
    return () => clearInterval(interval);
  }, [recordingIndex]);

  /* ================= START / STOP ================= */
  const handleStartListening = (idx) => {
  if (!speechSupported || listening) return;

  setLiveTranscript("");        // 🔑 reset live text
  activeIndexRef.current = idx;
  setRecordingIndex(idx);
  resetTranscript();

  startListening({
    continuous: false,
    interimResults: true,       // 🔑 REQUIRED for real-time
    lang: "en-GB",
    timeout: 4000,
  });
};


const handleStopListening = () => {
  try {
    stopListening();
  } catch {}

  setRecordingIndex(null);
};





  /* ================= TRANSCRIPT HANDLER ================= */
useEffect(() => {
  if (!transcript) return;

  // 🎤 LIVE speaking
  if (listening) {
    setLiveTranscript(transcript);
    return;
  }

  // 🛑 ignore stray updates
  if (activeIndexRef.current === null) return;

  const idx = activeIndexRef.current;
  if (!questions[idx]) return;

  const q = questions[idx];
  const spokenValue = liveTranscript || transcript;

  const isCorrect =
    normalize(spokenValue, q.requireCapitalization) ===
    normalize(q.textToPronounce, q.requireCapitalization);

  setAnswers((prev) => {
    const next = {
      ...prev,
      [idx]: { spoken: spokenValue, isCorrect },
    };

    const wordResults = compareWords(
      spokenValue,
      q.textToPronounce,
      q.requireCapitalization
    );

    setAttempt({
      exerciseId: exercise._id,
      attempts: [
        {
          questionIndex: idx,
          expectedText: q.textToPronounce,
          spokenText: spokenValue,
          wordResults,
          isCorrect,
          score: isCorrect ? 1 : 0,
          attemptedAt: new Date(),
        },
      ],
    });

    return next;
  });

  resetTranscript();
  setLiveTranscript("");
  activeIndexRef.current = null;
}, [transcript, listening]);


  /* ================= EDIT MODE ================= */
  if (isEditMode) {
    const updateQuestions = (next) =>
      onUpdate("pronunciationQuestions", next);

    const update = (idx, field, value) => {
      const next = [...questions];
      next[idx] = { ...next[idx], [field]: value };
      updateQuestions(next);
    };

    return (
      <div className="space-y-8">
        {questions.map((q, idx) => (
          <Card key={idx} className="p-6 space-y-4">
            <StringField
              label={`Text to Pronounce ${idx + 1}`}
              value={q.textToPronounce}
              onChange={(v) => update(idx, "textToPronounce", v)}
              required
              error={errors?.[`exercise-${exercise.index}-pron-text-${idx}`]}
            />

            {q.textToPronounce && (
              <SpeakerButton
                text={q.textToPronounce}
                lang="en-GB"
                onPlay={() => speakText(q.textToPronounce)}
              />
            )}

            <StringField
              label="IPA (optional)"
              value={q.ipaSound}
              onChange={(v) => update(idx, "ipaSound", v)}
            />

            <CapitalizationRule
              checked={q.requireCapitalization}
              onChange={(c) =>
                update(idx, "requireCapitalization", c)
              }
            />
          </Card>
        ))}
      </div>
    );
  }

  /* ================= STUDENT MODE ================= */
  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle>Pronunciation Practice</CardTitle>
        <CardDescription>
          Click the mic and pronounce each word or sentence
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-10">
        {questions.map((q, idx) => {
          const result = answers[idx];

          return (
            <div
              key={idx}
              className="border rounded-xl p-6 text-center space-y-6"
            >
              <div className="text-3xl font-bold text-blue-700">
                {q.textToPronounce}
              </div>

              <SpeakerButton
                text={q.textToPronounce}
                lang="en-GB"
                onPlay={() => speakText(q.textToPronounce)}
              />

              {speechSupported ? (
                <div className="flex justify-center gap-3">
                  {recordingIndex !== idx ? (
                    <Button
                      size="lg"
                      onClick={() => handleStartListening(idx)}
                      disabled={showResult}
                    >
                      🎤 Click to Speak
                    </Button>
                  ) : (
                    <>
                      <Button
                        size="lg"
                        variant="destructive"
                        className="animate-pulse"
                        disabled
                      >
                        🔴 Recording{dots}
                      </Button>
                      <Button
                        size="lg"
                        variant="outline"
                        onClick={handleStopListening}
                      >
                        ⏹ Stop
                      </Button>
                    </>
                  )}
                </div>
              ) : (
                <Input
                  placeholder="Type what you would say"
                  onBlur={(e) => {
                    const value = e.target.value;
                    const isCorrect =
                      normalize(value, q.requireCapitalization) ===
                      normalize(
                        q.textToPronounce,
                        q.requireCapitalization
                      );

                    setAnswers((prev) => ({
                      ...prev,
                      [idx]: { spoken: value, isCorrect },
                    }));
                  }}
                  className="max-w-md mx-auto"
                />
              )}

                {/* LIVE / FINAL SPOKEN TEXT */}
                  {(recordingIndex === idx || result?.spoken) && (
              <div className="mt-4 text-left max-w-md mx-auto">
                <p className="text-sm text-gray-500 font-medium">
                  📝 What we heard:
                </p>

                <div className="mt-1 p-3 border rounded-md bg-gray-50 text-gray-900 min-h-[48px]">
                  {recordingIndex === idx
                    ? liveTranscript || "Listening..."
                    : result?.spoken}
                </div>
              </div>
            )}



              {result && (
                <div
                  className={cn(
                    "text-lg font-semibold",
                    result.isCorrect
                      ? "text-green-700"
                      : "text-red-700"
                  )}
                >
                  {result.isCorrect ? "Correct! ✅" : "Try again 🔄"}
                  <div className="mt-3 flex flex-wrap justify-center gap-2">
  {compareWords(
    result.spoken,
    q.textToPronounce,
    q.requireCapitalization
  ).map((w, i) => (
    <span
      key={i}
      className={cn(
        "px-2 py-1 rounded text-sm font-semibold",
        w.status === "correct" && "bg-green-200 text-green-800",
        w.status === "wrong" && "bg-red-200 text-red-800",
        w.status === "missing" && "bg-yellow-200 text-yellow-800"
      )}
    >
      {w.word}
    </span>
  ))}
</div>

                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
